import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUser } from '../context/UserContext';
import { getTelegramWebApp, telegramStartPath } from '../utils/telegram';
import { telegramLogin, registerTelegramAccount, linkTelegramAccount, previewTelegramConnection, redeemTelegramConnection } from '../services/telegram';
import AppLoader from './AppLoader';
import {setAnalyticsLaunch} from '../utils/analytics';

const Screen = styled.main`
  height: var(--polka-visible-height, var(--polka-viewport-height, 100vh));
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: calc(24px + var(--polka-safe-top, 0px)) 24px calc(24px + var(--polka-safe-bottom, 0px));
`;
const Card = styled.section`
  margin: auto 0;
  width: 100%; max-width: 390px;
  h1 { font-size: 32px; margin-bottom: 12px; }
  p { line-height: 1.5; margin-bottom: 20px; color: ${({ theme }) => theme.colors.textSecondary}; }
  form { display: grid; gap: 16px; margin-top: 20px; }
  label { display: grid; gap: 8px; }
  input { width: 100%; padding: 14px; border-radius: 12px; border: 1px solid ${({ theme }) => theme.colors.textSecondary}; background: ${({ theme }) => theme.colors.surface}; color: ${({ theme }) => theme.colors.text}; font-size: 16px; }
`;
const Button = styled.button`
  width: 100%; padding: 14px 16px; margin-top: 10px; border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ $secondary, theme }) => $secondary ? 'transparent' : theme.colors.primary};
  color: ${({ $secondary, theme }) => $secondary ? theme.colors.text : theme.colors.onPrimary};
  font-size: 16px; font-weight: 600; cursor: pointer;
  &:disabled { opacity: .6; cursor: wait; }
`;

export default function TelegramAuthGate({ children }) {
  const app = getTelegramWebApp();
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const [phase, setPhase] = useState(app ? 'checking' : 'ready');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [connectionProfile, setConnectionProfile] = useState(null);
  const startParam = app ? new URLSearchParams(app.initData).get('start_param') : null;
  const connectionCode = /^connect_([A-Za-z0-9_-]{32})$/.exec(startParam || '')?.[1];

  const accept = (session) => {
    if (!session.token || !session.user?.id) throw new Error('Не удалось получить профиль');
    localStorage.setItem('polka_token', session.token);
    setAnalyticsLaunch(session.analytics_ticket);
    setUser(session.user);
    setPhase('ready');
    const target = typeof session.launch_path==='string'&&/^\/(?:item\/(?:film|series|game|music|book)\/[^/?#]+|notifications|friends|profile|collections(?:\/[^/?#]+)?|shared-collections(?:\/[^/?#]+)?|fandom\/[^/?#]+)?$/.test(session.launch_path)?session.launch_path:telegramStartPath(startParam);
    if (connectionCode) navigate('/settings', { replace: true });
    else if (target) navigate(target, { replace: true });
    else if (['/login', '/register'].includes(window.location.pathname)) navigate('/', { replace: true });
  };

  useEffect(() => {
    if (!app) return;
    let active = true;
    // A cached website session is never implicitly linked to a Telegram identity.
    localStorage.removeItem('polka_token');
    if (connectionCode) {
      previewTelegramConnection(app.initData, connectionCode).then(profile => {
        if (active) { setConnectionProfile(profile); setPhase('connect'); }
      }).catch(err => { if (active) { setError(err.message); setPhase('error'); } });
      return () => { active = false; };
    }
    telegramLogin(app.initData).then(session => {
      if (!active) return;
      if (session.requires_account_choice) setPhase('choice');
      else accept(session);
    }).catch(err => {
      if (active) { setError(err.message); setPhase('error'); }
    });
    return () => { active = false; };
    // Only authenticate at launch; logout must not trigger automatic re-login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (action) => {
    setBusy(true); setError('');
    try {
      const session = await action();
      if (session.requires_account_choice) setPhase('choice');
      else accept(session);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (!app || (phase === 'ready' && user)) return children;
  if (phase === 'checking') return <AppLoader fullScreen label="Входим через Telegram…" />;
  return <Screen><Card>
    <h1>Полка</h1>
    <>
      <p>{phase === 'connect' ? `Подключить Telegram к профилю «${connectionProfile?.name || connectionProfile?.username}» (@${connectionProfile?.username})? Полка и друзья останутся в этом профиле.` : phase === 'choice' || phase === 'link'
        ? 'Собирайте любимое здесь. Если у вас уже есть «Полка», привяжите профиль — ваши коллекции и друзья сохранятся.'
        : phase === 'ready' ? 'Вы вышли из профиля «Полки».' : 'Вход через Telegram недоступен.'}</p>
      {error && <p role="alert">{error}</p>}
      {phase === 'connect' && <>
        <Button disabled={busy} onClick={() => run(() => redeemTelegramConnection(app.initData, connectionCode))}>Подключить этот профиль</Button>
        <Button $secondary disabled={busy} onClick={() => run(() => telegramLogin(app.initData))}>Отмена</Button>
      </>}
      {phase === 'choice' && <>
        <Button disabled={busy} onClick={() => run(() => registerTelegramAccount(app))}>Создать профиль</Button>
        <p>После разрешения Telegram будем присылать уведомления о лайках, комментариях и друзьях. Их можно отключить в настройках.</p>
        <Button $secondary disabled={busy} onClick={() => { setError(''); setPhase('link'); }}>У меня уже есть «Полка»</Button>
      </>}
      {phase === 'link' && <form onSubmit={event => { event.preventDefault(); run(() => linkTelegramAccount(app.initData, credentials)); }}>
        <label>Email или имя пользователя<input autoComplete="username" required value={credentials.email} onChange={event => setCredentials({ ...credentials, email: event.target.value })} /></label>
        <label>Пароль<input type="password" autoComplete="current-password" required value={credentials.password} onChange={event => setCredentials({ ...credentials, password: event.target.value })} /></label>
        <Button disabled={busy} type="submit">{busy ? 'Привязываем…' : 'Войти и привязать Telegram'}</Button>
        <Button $secondary type="button" onClick={() => app.openLink('https://plkapp.ru/settings?connect=telegram')}>Войти через VK или Яндекс на сайте</Button>
        <p>После входа откройте «Аккаунт → Подключить Telegram». Одноразовая ссылка вернёт вас сюда для подтверждения профиля.</p>
        <Button $secondary disabled={busy} type="button" onClick={() => { setCredentials({ email: '', password: '' }); setError(''); setPhase('choice'); }}>Назад</Button>
      </form>}
      {(phase === 'error' || phase === 'ready') && <Button disabled={busy} onClick={() => run(() => telegramLogin(app.initData))}>Войти через Telegram</Button>}
    </>
  </Card></Screen>;
}
